import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTransactions } from 'store/transactions/useTransactions';
import { IRemoveVaultFactoryForm } from 'components/onboarding/deployment/type';
import { updateVaultFactorySchema } from 'components/onboarding/deployment/formSchema';
import { FormError } from 'components/onboarding/form/FormError';
import {
  Heading,
  Stack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Text,
  Box,
  Grid,
  GridItem
} from '@chakra-ui/react';
import { FC } from 'react';
import { useWeb3React } from 'hooks/web3';
import { useConfig } from 'store/config';
import {
  mapPowerSwitchStatusToDescription,
  PowerSwitchStatus
} from 'api/blockchain/rewardProgram';
import { useModal } from 'store/modals';
import { ModalType } from 'components/modals/types';
import { ethers } from 'ethers';

type Props = {
  vaultFactories: { id: string }[];
  programState?: PowerSwitchStatus;
  aludelAbi: ethers.ContractInterface;
};

export const RemoveVaultFactoryForm: FC<Props> = ({
  vaultFactories,
  programState,
  aludelAbi
}) => {
  const { updateVaultFactory } = useTransactions();
  const { rewardProgramAddress } =
    useParams<{ rewardProgramAddress: string }>();
  const { chainId = 1 } = useWeb3React();
  const { crucibleFactoryAddress } = useConfig(chainId).config;
  const { openModal } = useModal();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<IRemoveVaultFactoryForm>({
    resolver: yupResolver(updateVaultFactorySchema),
    mode: 'onBlur',
    defaultValues: {
      address: ''
    }
  });

  const updateVaultFactoryAction = async ({ address }: { address: string }) => {
    if (address.toLowerCase() === crucibleFactoryAddress.toLowerCase()) {
      openModal(ModalType.removeVaultFactory, {
        rewardProgramAddress,
        address,
        method: 'remove',
        aludelAbi
      });
      return;
    }
    await updateVaultFactory(
      rewardProgramAddress,
      address,
      'remove',
      aludelAbi
    );
  };

  return (
    <>
      <Heading size='md' mb={6}>
        Remove Vault Factory
      </Heading>
      <Text color='gray.200' mb={6}>
        This option removes the vault factory specified from the reward program.
        A vault factory is, for example, the Crucible contract. When removing a
        vault factory, it means that vault can no longer stake to the reward
        program. Users currently staked can still unstake as per usual.
      </Text>
      <Box p={4} background='gray.700' borderRadius={8} mb={8}>
        <Heading size='sm' mb={1}>
          Registered vault factories
        </Heading>
        <Grid
          templateColumns={['repeat(1, 1fr)', null, null, 'repeat(4, 1fr)']}
          gap={[null, null, null, 5]}
          width='fit-content'
          alignItems={'center'}
        >
          {vaultFactories.length > 0 &&
            vaultFactories.map((factory) => (
              <>
                <GridItem colSpan={3}>
                  <Text>
                    {factory.id.split('-')[1]}
                    {factory.id.split('-')[1].toLowerCase() ===
                    crucibleFactoryAddress.toLowerCase()
                      ? ' (Crucible Factory)'
                      : ''}
                  </Text>
                </GridItem>
                <GridItem colSpan={1}>
                  <Button
                    borderRadius={'lg'}
                    disabled={programState === PowerSwitchStatus.SHUTDOWN}
                    onClick={() =>
                      updateVaultFactoryAction({
                        address: factory.id.split('-')[1]
                      })
                    }
                  >
                    {programState === PowerSwitchStatus.SHUTDOWN
                      ? mapPowerSwitchStatusToDescription[programState]
                      : 'Remove'}
                  </Button>
                </GridItem>
              </>
            ))}
        </Grid>
        {vaultFactories.length === 0 && (
          <Text>No vault factories registered</Text>
        )}
      </Box>
      <Stack
        direction={['column', 'row']}
        alignItems={['flex-start', 'flex-end']}
        spacing={4}
      >
        <FormControl width='max-content' isRequired>
          <FormLabel>Enter Address</FormLabel>
          <Input
            id='address'
            type='text'
            borderColor={'gray.300'}
            placeholder='0x'
            {...register('address')}
          />
          {errors.address && <FormError>{errors.address.message}</FormError>}
        </FormControl>
        <Button
          px={6}
          type='submit'
          borderRadius={'lg'}
          disabled={programState === PowerSwitchStatus.SHUTDOWN}
          onClick={handleSubmit((d) => updateVaultFactoryAction(d))}
        >
          {programState === PowerSwitchStatus.SHUTDOWN
            ? mapPowerSwitchStatusToDescription[programState]
            : 'Remove Vault Factory'}
        </Button>
      </Stack>
    </>
  );
};
